import { useState, useEffect, useRef, useCallback } from 'react'

import { catalogItemDisplayName, searchCatalogItems, type CatalogItem } from '../../services/catalog-service'
import { Modal } from '../ui'

export type { CatalogItem }

interface ItemPickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (item: CatalogItem) => void
  nature?: 'GOODS' | 'SERVICES'
}

interface UseItemPickerStateReturn {
  search: string
  setSearch: React.Dispatch<React.SetStateAction<string>>
  items: CatalogItem[]
  setItems: React.Dispatch<React.SetStateAction<CatalogItem[]>>
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  highlightedIndex: number
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>
  searchRef: React.RefObject<HTMLInputElement>
  listRef: React.RefObject<HTMLDivElement>
  debounceRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>
}

function useItemPickerState(): UseItemPickerStateReturn {
  const [search, setSearch] = useState('')
  const [items, setItems] = useState<CatalogItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  return { search, setSearch, items, setItems, isLoading, setIsLoading, highlightedIndex, setHighlightedIndex, searchRef, listRef, debounceRef }
}

function buildHandleKeyDown(items: CatalogItem[], highlightedIndex: number, setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>, handleSelect: (item: CatalogItem) => void, onClose: () => void): (e: React.KeyboardEvent<HTMLInputElement>) => void {
  return (e) => {
    if (items.length === 0) { return }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((i) => (i < items.length - 1 ? i + 1 : 0))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((i) => (i > 0 ? i - 1 : items.length - 1))
        break
      case 'Enter':
        e.preventDefault()
        if (items[highlightedIndex]) { handleSelect(items[highlightedIndex]) }
        break
      case 'Escape':
        onClose()
        break
      default:
        break
    }
  }
}

function ItemPickerList({ isLoading, items, highlightedIndex, listRef, onSelect, onHighlight }: {
  isLoading: boolean
  items: CatalogItem[]
  highlightedIndex: number
  listRef: React.RefObject<HTMLDivElement>
  onSelect: (item: CatalogItem) => void
  onHighlight: (idx: number) => void
}): JSX.Element {
  return (
    <div ref={listRef} className="flex flex-col divide-y divide-border max-h-80 overflow-y-auto rounded border border-border">
      {isLoading && <div className="py-8 text-center text-sm text-muted-foreground">Searching…</div>}
      {!isLoading && items.length === 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground">No items found</div>
      )}
      {!isLoading && items.map((item, idx) => (
        <ItemRow
          key={item.id}
          isHighlighted={idx === highlightedIndex}
          item={item}
          onHighlight={() => onHighlight(idx)}
          onSelect={() => onSelect(item)}
        />
      ))}
    </div>
  )
}

function ItemRow({ item, isHighlighted, onSelect, onHighlight }: { item: CatalogItem; isHighlighted: boolean; onSelect: () => void; onHighlight: () => void }): JSX.Element {
  return (
    <button
      className={`flex items-center gap-3 px-3 py-2.5 text-left w-full transition-colors ${isHighlighted ? 'bg-primary/10 text-foreground' : 'hover:bg-muted'}`}
      type="button"
      onClick={onSelect}
      onMouseEnter={onHighlight}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{catalogItemDisplayName(item)}</p>
        <p className="text-xs text-muted-foreground truncate">
          {item.code}
          {item.hsnCode ? ` · HSN ${item.hsnCode}` : ''}
          {item.sacCode ? ` · SAC ${item.sacCode}` : ''}
          {(item.baseUom?.symbol ?? item.baseUom?.name) ? ` · ${item.baseUom?.symbol ?? item.baseUom?.name}` : ''}
        </p>
      </div>
      <span className={`shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded ${item.nature === 'GOODS' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'}`}>
        {item.nature === 'GOODS' ? 'Goods' : 'Service'}
      </span>
    </button>
  )
}

function computeTitle(nature?: 'GOODS' | 'SERVICES'): string {
  if (nature === 'GOODS') { return 'Pick a Product' }
  if (nature === 'SERVICES') { return 'Pick a Service' }
  return 'Pick an Item'
}

function useItemPickerEffects(state: UseItemPickerStateReturn, isOpen: boolean, nature?: 'GOODS' | 'SERVICES'): void {
  const { setSearch, setItems, setHighlightedIndex, searchRef, listRef, setIsLoading, highlightedIndex } = state
  useEffect(() => {
    if (!isOpen) {
      setSearch('')
      setItems([])
      setHighlightedIndex(0)
      return
    }
    const raf = requestAnimationFrame(() => { searchRef.current?.focus() })
    setIsLoading(true)
    void searchCatalogItems('', nature).then((results) => {
      setItems(results)
      setIsLoading(false)
    })
    return () => cancelAnimationFrame(raf)
  }, [isOpen, nature, setSearch, setItems, setHighlightedIndex, searchRef, setIsLoading])

  useEffect(() => {
    if (!listRef.current) { return }
    const child = listRef.current.children[highlightedIndex] as HTMLElement | undefined
    child?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, listRef])
}

export function ItemPickerModal({ isOpen, onClose, onSelect, nature }: ItemPickerModalProps): JSX.Element {
  const state = useItemPickerState()
  const { search, items, isLoading, highlightedIndex, setHighlightedIndex, setIsLoading, setSearch, setItems, searchRef, listRef, debounceRef } = state

  useItemPickerEffects(state, isOpen, nature)

  const handleSearch = (value: string): void => {
    setSearch(value)
    setHighlightedIndex(0)
    if (debounceRef.current) { clearTimeout(debounceRef.current) }
    debounceRef.current = setTimeout(() => {
      setIsLoading(true)
      void searchCatalogItems(value, nature).then((results) => {
        setItems(results)
        setIsLoading(false)
      })
    }, 300)
  }

  const handleSelect = useCallback((item: CatalogItem) => {
    onSelect(item)
    onClose()
  }, [onSelect, onClose])

  const handleKeyDown = buildHandleKeyDown(items, highlightedIndex, setHighlightedIndex, handleSelect, onClose)

  return (
    <Modal isOpen={isOpen} size="md" title={computeTitle(nature)} onClose={onClose}>
      <div className="flex flex-col gap-3">
        <input
          ref={searchRef}
          className="w-full border border-border rounded px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="Search items… (↑↓ to navigate, Enter to select)"
          type="text"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <ItemPickerList
          highlightedIndex={highlightedIndex}
          isLoading={isLoading}
          items={items}
          listRef={listRef}
          onHighlight={setHighlightedIndex}
          onSelect={handleSelect}
        />
      </div>
    </Modal>
  )
}
